import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Github, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useProjects, usePageMeta } from "@/hooks/useProjects";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { PageWipe, SplitWords, Parallax } from "@/components/site/Motion";

const ease = [0.22, 1, 0.36, 1];

const Block = ({ title, children, testid }) => (
  <Reveal>
    <div data-testid={testid} className="border-t-2 border-slate-900/10 pt-8">
      <h2 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-slate-500">{title}</h2>
      <div className="mt-4">{children}</div>
    </div>
  </Reveal>
);

const Features = ({ p }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    {p.features.map((f, i) => (
      <motion.div key={i} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.6, delay: (i % 2) * 0.1, ease }} className="group rounded-3xl bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.05)] ring-1 ring-slate-900/5 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(15,23,42,0.1)]">
        <span className="mb-4 block h-2 w-10 rounded-full transition-[width] duration-500 group-hover:w-20" style={{ background: p.accent }} />
        <h3 className="font-display text-lg font-extrabold text-slate-900">{f.title}</h3>
        {f.text && <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{f.text}</p>}
      </motion.div>
    ))}
  </div>
);

const Stats = ({ p }) => (
  <div data-testid="project-stats" className="grid gap-4 sm:grid-cols-3">
    {p.stats.map((s, i) => (
      <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, delay: i * 0.1, ease }} className="rounded-3xl p-6 text-white" style={{ background: i % 2 ? "#0F172A" : p.accent }}>
        <p className="font-display text-5xl font-extrabold">{s.value}</p>
        <p className="mt-1 font-medium text-white/90">{s.label}</p>
      </motion.div>
    ))}
  </div>
);

const Links = ({ p }) => (
  <div className="flex flex-wrap gap-3">
    {p.live_url && <a data-testid="project-live-link" href={p.live_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 font-bold text-white hover:bg-[#0055FF]">Visit live project <ArrowUpRight className="h-4 w-4" /></a>}
    {p.source_url && <a data-testid="project-source-link" href={p.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 px-6 py-3 font-bold text-slate-900 hover:bg-slate-900 hover:text-white"><Github className="h-4 w-4" /> Source code</a>}
  </div>
);

const Body = ({ p }) => (
  <div className="container-x grid gap-12 py-16 lg:grid-cols-[1fr_2fr]">
    <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
      {p.client && <div><p className="text-sm font-bold uppercase tracking-[0.16em] text-slate-500">Client</p><p className="mt-1 font-display text-xl font-bold">{p.client}</p></div>}
      <div><p className="text-sm font-bold uppercase tracking-[0.16em] text-slate-500">Category</p><p className="mt-1 font-display text-xl font-bold">{p.category}</p></div>
      {p.role && <div><p className="text-sm font-bold uppercase tracking-[0.16em] text-slate-500">Our role</p><p className="mt-1 text-slate-700">{p.role}</p></div>}
      {p.technologies.length > 0 && <div><p className="text-sm font-bold uppercase tracking-[0.16em] text-slate-500">Tools & platforms</p><div className="mt-2 flex flex-wrap gap-2">{p.technologies.map((t) => <span key={t} className="rounded-full bg-white px-3 py-1 text-sm font-bold ring-1 ring-slate-900/10">{t}</span>)}</div></div>}
      {(p.live_url || p.source_url) && <Links p={p} />}
    </aside>
    <div className="space-y-12">
      {p.overview && <Block title="Overview" testid="project-overview"><p className="text-lg leading-relaxed text-slate-700 sm:text-xl">{p.overview}</p></Block>}
      {p.objective && <Block title="Objective" testid="project-objective"><p className="text-lg leading-relaxed text-slate-700">{p.objective}</p></Block>}
      {p.stats.length > 0 && <Block title="By the numbers" testid="project-numbers"><Stats p={p} /></Block>}
      {p.features.length > 0 && <Block title="Features & highlights" testid="project-features"><Features p={p} /></Block>}
      {p.results.length > 0 && <Block title="Results" testid="project-results"><ul className="space-y-3">{p.results.map((r) => <li key={r} className="flex gap-3 text-lg text-slate-800"><CheckCircle2 className="mt-1 h-5 w-5 shrink-0" style={{ color: p.accent }} />{r}</li>)}</ul></Block>}
    </div>
  </div>
);

const Next = ({ slug }) => {
  const { projects } = useProjects();
  const i = projects.findIndex((x) => x.slug === slug);
  const next = projects.length > 1 && i >= 0 ? projects[(i + 1) % projects.length] : null;
  if (!next) return null;
  return (
    <Link to={`/work/${next.slug}`} data-testid="next-project-link" className="group block py-16 text-white transition-colors" style={{ background: next.accent }}>
      <div className="container-x flex items-center justify-between gap-6">
        <div><p className="text-sm font-bold uppercase tracking-[0.18em] text-white/90">Next project</p><p className="mt-2 font-display text-3xl font-extrabold transition-transform duration-500 group-hover:translate-x-3 sm:text-5xl">{next.title}</p></div>
        <ArrowUpRight className="h-12 w-12 shrink-0 transition-transform group-hover:rotate-45" />
      </div>
    </Link>
  );
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [state, setState] = useState("loading");
  usePageMeta(p ? `${p.title} | ${p.category} | OURUMDIGITAL` : "Project | OURUMDIGITAL", p?.summary);

  useEffect(() => {
    setState("loading");
    window.scrollTo(0, 0);
    api.get(`/projects/${slug}`).then((r) => { setP(r.data); setState("ok"); }).catch(() => setState("missing"));
  }, [slug]);

  return (
    <div data-testid="project-detail-page" className="bg-[#FAF9F5]">
      <Navbar />
      {state === "loading" && <div className="container-x pt-40"><div className="h-[420px] animate-pulse rounded-[32px] bg-[#F4F1EA]" /></div>}
      {state === "missing" && (
        <div className="container-x py-48 text-center"><h1 className="font-display text-4xl font-extrabold">Project not found</h1><Link to="/#work" className="mt-6 inline-block rounded-full bg-[#0055FF] px-6 py-3 font-bold text-white">Back to work</Link></div>
      )}
      {state === "ok" && p && (
        <>
          <PageWipe key={p.slug} color={p.accent} />
          <header className="relative overflow-hidden pt-32" style={{ background: `${p.accent}14` }}>
            <Parallax speed={60} className="pointer-events-none absolute -right-20 top-20 h-72 w-72 rounded-full" >
              <div className="h-72 w-72 rounded-full opacity-30 blur-3xl" style={{ background: p.accent }} />
            </Parallax>
            <div className="container-x relative">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}>
                <Link to="/#work" data-testid="back-to-work" className="group inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold ring-1 ring-slate-900/10 transition-colors hover:bg-slate-900 hover:text-white"><ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" /> All work</Link>
                <span className="ml-3 inline-block rounded-full px-3 py-1.5 text-xs font-bold text-white" style={{ background: p.accent === "#FACC15" || p.accent === "#84CC16" ? "#0F172A" : p.accent }}>{p.category}</span>
              </motion.div>
              <h1 data-testid="project-title" className="mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[1.02] tracking-[-0.03em] text-slate-900 sm:text-5xl lg:text-7xl"><SplitWords inView={false} delay={0.5} text={p.title} /></h1>
              {p.tagline && <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }} className="mt-5 max-w-2xl text-lg text-slate-700 sm:text-xl">{p.tagline}</motion.p>}
              <motion.div initial={{ opacity: 0, y: 80 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 1.1, ease }} className="relative mt-12 overflow-hidden rounded-t-[32px] p-3 sm:p-5" style={{ background: p.accent }}>
                <motion.img initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ delay: 1, duration: 1.4, ease }} src={p.cover_image} alt={`${p.title} main visual`} className="mx-auto max-h-[640px] w-full rounded-t-[24px] bg-[#F4F1EA] object-contain p-3 sm:p-6" />
              </motion.div>
            </div>
          </header>
          <Body p={p} />
          <Next slug={p.slug} />
        </>
      )}
      <Footer />
    </div>
  );
}
